import { findStateHints, isStateIgnoredIn, type HintLink, type IgnoredTerm, type StateHint, type StateRole } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestAct, type SuggestLive, type SuggestState } from './suggest';

/**
 * Mögliche Zustandsänderungen im Draft einer Szene (PLAN.md §16c): Signalwörter neben einem verknüpften Element („Clara stirbt“, „reist
 * nach Rom“, „nimmt den Ring“) sind als Vorschlag markiert (`suggest.ts`); ✓ hält die Änderung in der Szene fest, ✕ merkt „keine
 * Änderung“ im Dokument. Schon festgehaltene bleiben dezent unterstrichen.
 */

export type StateAct = SuggestAct;

export interface StateLive extends SuggestLive<Hit> {
  /** Rolle eines Verweisschlüssels im Zustand (null: kein Element) */
  roleOf: (key: string) => StateRole | null;
  /** schon als Änderung der Szene festgehalten */
  isSet: (hint: StateHint) => boolean;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hint: StateHint, set: boolean) => string;
}

type Hit = StateHint & { set: boolean };

export const stateHintKey = new PluginKey<SuggestState<Hit>>('stateHints');

const ELSEWHERE = new Set(['rede', 'gedanke']);

/** Vorschläge im Dokument (mit ihrer Lage im Dokument), je Absatz; ohne ignorierte. */
export function stateHits(doc: PMNode, live: Pick<StateLive, 'roleOf' | 'isSet' | 'ignored'>): Hit[] {
  const out: Hit[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (node.type.spec.code) return false;
    const text = node.textBetween(0, node.content.size, '\n', '\n');
    const links: HintLink[] = [];
    const quoted: [number, number][] = [];
    node.forEach((child, offset) => {
      if (!child.isText) return;
      const end = offset + child.nodeSize;
      if (child.marks.some((m) => m.type.name === 'refSpan' && ELSEWHERE.has(m.attrs.role as string))) quoted.push([offset, end]);
      const key = child.marks.find((m) => m.type.name === 'refLink')?.attrs.key as string | undefined;
      if (!key) return;
      const last = links[links.length - 1];
      if (last && last.key === key && last.to === offset) {
        last.to = end;
        return;
      }
      const role = live.roleOf(key);
      if (role) links.push({ key, from: offset, to: end, role });
    });
    if (links.length === 0) return false;
    for (const h of findStateHints(text, links, quoted)) {
      if (isStateIgnoredIn(live.ignored, h)) continue;
      out.push({ ...h, from: pos + 1 + h.from, to: pos + 1 + h.to, set: live.isSet(h) });
    }
    return false;
  });
  return out;
}

export const stateHints = (live: { current: StateLive }) =>
  suggestExtension<Hit, StateLive>(
    {
      name: 'stateHints',
      kind: 'state',
      key: stateHintKey,
      hits: (doc, l) => stateHits(doc, l),
      mark: (h, l) => ({ class: h.set ? 'rt-state-set' : 'rt-state-hint', 'data-state-hint': `${h.key}:${h.kind}:${h.value ?? ''}`, title: l.title(h, h.set) }),
      done: (h) => h.set,
      word: (h) => h.word,
      testId: 'state',
    },
    live,
  );
